import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';

import { useDeleteSystemComponent } from 'src/features/systems/api/systemComponents/useDeleteSystemComponent';
import { LIST_SYSTEM_COMPONENTS_RESOURCE } from 'src/features/systems/api/systemComponents/useListSystemComponents';
import { systemsMessages } from 'src/features/systems/utils/messages';
import { SystemComponent } from 'src/features/systems/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalErrorMessages, generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

interface SystemComponentFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedSystemComponent: SystemComponent;
    children?: React.ReactNode;
}

function DeleteSystemComponentFormModal<TData>({
    selectedSystemComponent,
    semantics,
    children,
}: SystemComponentFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const { mutate: deleteSystemComponent, isPending: isPendingDelete } = useDeleteSystemComponent({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SYSTEM_COMPONENTS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(systemsMessages.deleteComponentSuccess),
                description: formatMessage(systemsMessages.deleteComponentSuccessDescription, {
                    entry: selectedSystemComponent?.name,
                }),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const onSubmit = () => deleteSystemComponent({ componentId: selectedSystemComponent.id, parkingId });

    return (
        <Dialog modal open={isModalOpen} onOpenChange={setIsModalOpen}>
            {children && <DialogTrigger asChild>{children}</DialogTrigger>}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={ICON_MAP.DELETE} />
                        <div>
                            <DialogTitle>
                                <FormattedMessage
                                    id={generalMessages.deleteEntry.id}
                                    defaultMessage={generalMessages.deleteEntry.defaultMessage}
                                    values={{ entry: semantics?.entryName?.toLowerCase() }}
                                />
                            </DialogTitle>
                            <DialogDescription>
                                <FormattedMessage
                                    id={systemsMessages.deleteComponentSubtitle.id}
                                    defaultMessage={systemsMessages.deleteComponentSubtitle.defaultMessage}
                                    values={{ entry: selectedSystemComponent.name }}
                                />
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <p className="text-gray-500">
                        <FormattedMessage
                            id={systemsMessages.confirmDeleteComponent.id}
                            defaultMessage={systemsMessages.confirmDeleteComponent.defaultMessage}
                            values={{ entry: selectedSystemComponent?.name }}
                        />
                    </p>
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage
                                id={generalMessages.cancel.id}
                                defaultMessage={generalMessages.cancel.defaultMessage}
                            />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="destructiveLight"
                        disabled={isPendingDelete}
                        type="submit"
                        onClick={onSubmit}
                        className="flex-1"
                    >
                        {isPendingDelete && (
                            <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />
                        )}
                        <FormattedMessage
                            id={generalMessages.deleteEntry.id}
                            defaultMessage={generalMessages.deleteEntry.defaultMessage}
                            values={{ entry: semantics?.entryName?.toLowerCase() }}
                        />
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default DeleteSystemComponentFormModal;
